import * as React from "react";
import { useEffect, useState } from "react";
import { Modal, Stack } from "@fluentui/react";
import HeaderPanel from "../layout/HeaderPanel";
import FooterPanel from "../layout/FooterPanel";
import { ButtonVariants } from "../../styles/fluent/ButtonVariants";
import { formatMessage } from "../../utils/helpers/LanguageHelper";
import { PrimaryButton } from "@fluentui/react/lib/Button";
import { DependentSelectionsModelProvider } from "../generic/dependent-selections/DependentSelectionsModel";
import { fetchObjectComponentInfoItems, ObjectComponentInfoItem } from "../../api/object/ObjectComponentApi";
import { SearchBar } from "../generic/searchBar/SearchBar";
import { PageData } from "../../api/object/PageResult";
import { Pager } from "../generic/Pager";
import { CoObjectTypeEnum } from "../../api/models";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
/* global console */

export interface ObjectComponentReferenceSelectorProps {
  onComponentSelected: (componentInfoItem?: ObjectComponentInfoItem) => void;
  dependentSelectionsModelProvider: DependentSelectionsModelProvider;
}

interface ObjectComponentReferenceSelectorState {
  selectedObjectComponentInfoItem?: ObjectComponentInfoItem;
}

/**
 * Component for selecting object component
 * @param props
 * onComponentSelected: callback for selecting object component
 * dependentSelectionsModelProvider: model provider for dependent selections
 */
export const ObjectComponentReferenceSelector: React.FunctionComponent<ObjectComponentReferenceSelectorProps> = (
  props
) => {
  const { onComponentSelected } = props;
  const [state, setState] = useState<ObjectComponentReferenceSelectorState>({});
  const { selectedObjectComponentInfoItem } = state;

  return (
    <Modal
      titleAriaId={"titleId"}
      isOpen={true}
      scrollableContentClassName={"no_scroll"}
      styles={{
        main: {
          minHeight: "100%",
          minWidth: "100%",
        },
      }}
    >
      <HeaderPanel applyPadding headerRowJustifyContent={"space-between"}>
        {formatMessage("cof.createnewobject.componentselection")}
      </HeaderPanel>
      <ObjectComponentSearchPane
        selectedObjectComponentInfoItem={selectedObjectComponentInfoItem}
        onSelectObjectComponentInfoItem={(item) => setState({ ...state, selectedObjectComponentInfoItem: item })}
      ></ObjectComponentSearchPane>
      <FooterPanel>
        <PrimaryButton
          data-testid="Back"
          className={ButtonVariants.white}
          text={formatMessage("cof.base.previous")}
          onClick={() => {
            onComponentSelected(undefined);
          }}
          style={{ float: "left" }}
        />
        <PrimaryButton
          data-testid="select-component"
          id={"select-component"}
          disabled={!selectedObjectComponentInfoItem}
          text={formatMessage("cof.base.select")}
          onClick={() => {
            onComponentSelected(selectedObjectComponentInfoItem);
          }}
          className={ButtonVariants.primary}
          style={{ float: "right" }}
        />
      </FooterPanel>
    </Modal>
  );
};

/**
 * Component Pane for searching object components
 */
interface ObjectComponentSearchPaneProps {
  selectedObjectComponentInfoItem?: ObjectComponentInfoItem;
  onSelectObjectComponentInfoItem: (item: ObjectComponentInfoItem) => void;
}

interface ObjectComponentSearchPaneState {
  searchValue: string;
  items: ObjectComponentInfoItem[];
  page: PageData;
}

const INITIAL_PAGE_DATA = {
  number: 0,
  size: 12,
};

/**
 * Component Pane for searching object components
 * @param props
 *  selectedObjectComponentInfoItem: selected object component
 *  onSelectObjectComponentInfoItem: callback for selecting object component
 */
const ObjectComponentSearchPane: React.FunctionComponent<ObjectComponentSearchPaneProps> = (props) => {
  const [state, setState] = useState<ObjectComponentSearchPaneState>({
    searchValue: "",
    items: [],
    page: INITIAL_PAGE_DATA,
  });
  const { selectedObjectComponentInfoItem, onSelectObjectComponentInfoItem } = props;

  const { searchValue, items, page } = state;

  useEffect(() => {
    if ( searchValue.length % 3 === 0) {
      fetchObjectComponentInfoItems(searchValue, page.number, page.size).then((pagedResult) => {
        if (pagedResult._embedded) {
          setState({
            ...state,
            items: pagedResult._embedded.componentTOList?.map((item) => ({
              ...item,
              type: item.type === CoObjectTypeEnum.AMOUNT_AIH ? CoObjectTypeEnum.AMOUNT : item.type,
            })),
            page: pagedResult.page,
          });
        } else {
          setState({ ...state, items: [], page: INITIAL_PAGE_DATA });
        }
      });
    }
  }, [searchValue, page.number]);

  const updatePageNumber = (pageNumber: number) => {
    setState({ ...state, page: { ...page, number: pageNumber } });
  };

  return (
    <div>
      <SearchBar onChangeValue={(event) => setState({ ...state, searchValue: event.value, page: INITIAL_PAGE_DATA })} />
      <div style={{ height: "65vh", overflow: "scroll", overflowY: "auto", overflowX: "hidden" }}>
        {items.map((item) => (
          <ObjectComponentInfoItemRow
            key={item.objectLogicId}
            item={item}
            selected={selectedObjectComponentInfoItem?.objectLogicId == item.objectLogicId}
            onSelectObjectComponentInfoItem={onSelectObjectComponentInfoItem}
          />
        ))}
      </div>
      <Pager page={page} updatePageNumber={updatePageNumber} />
    </div>
  );
};

/**
 * Component for displaying object component info item
 * @param props
 *  item: object component info item
 *  selected: is selected
 *  onSelectObjectComponentInfoItem: callback for selecting object component
 */
const ObjectComponentInfoItemRow = (props: {
  item: ObjectComponentInfoItem;
  selected: boolean;
  onSelectObjectComponentInfoItem: (item: ObjectComponentInfoItem) => void;
}) => {
  const { item, selected, onSelectObjectComponentInfoItem } = props;
  return (
    <div
      className={`component_item_row ${selected ? "component_item_row_selected" : ""}`}
      onClick={() => onSelectObjectComponentInfoItem(item)}
    >
      <Stack horizontal horizontalAlign="space-between">
        <div className={`${item.type} icon_class`}>
          {item.objectName}
          <span className="component_item_icon">{"  "}</span>
        </div>
        <div className={`component_item_row_doc_name ${selected ? "component_item_row_doc_name_selected" : ""}`}>
          {item.documentName}
        </div>
      </Stack>
    </div>
  );
};
